import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ColumnDef } from "@tanstack/react-table";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { PAGE_TITLES } from "~/configs/pageTitles";
import { useLinkDetailPageTitle } from "~/hooks/usePageTitle";
import { routes } from "~/router";

import { TaskAttributes, Task as TaskType } from "../../../api/Api";
import { apiClient } from "../../../api/ApiClient";
import { Icon } from "../../../assets/icons/Icon";
import Button from "../../../components/ui/button/Button";
import Card from "../../../components/ui/card/Card";
import DataTable from "../../../components/ui/dataTable/DataTable";
import { shortenUrl } from "../../../helpers/helpers";
import styles from "./OutreachLinkDetailPage.module.scss";

// Reusing same styles

// Define the response type that matches the actual API response
interface TaskResponse {
  task: TaskType;
}

// Status badge component
const StatusBadge = ({ status }: { status: string }) => {
  const getStatusClass = () => {
    switch (status) {
      case "DONE":
        return styles.statusDone;
      case "DOING":
        return styles.statusDoing;
      case "CANCELLED":
        return styles.statusCancelled;
      case "TODO":
      default:
        return styles.statusTodo;
    }
  };

  return (
    <span className={`${styles.statusBadge} ${getStatusClass()}`}>
      {status}
    </span>
  );
};

const NicheLinkDetailPage = () => {
  const { id: siteId, taskId } = useParams();

  if (!siteId) {
    throw Error("No site id provided");
  }

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [notes, setNotes] = useState("");
  const [notesInitialized, setNotesInitialized] = useState(false);
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage] = useState(10);
  const [searchQuery] = useState("");

  // Fetch niche link data
  const {
    data: taskResponse,
    isLoading,
    error,
  } = useQuery<TaskResponse>({
    queryKey: ["nicheTask", siteId, taskId],
    queryFn: async () => {
      try {
        // Convert IDs to numbers and validate
        const numericSiteId = Number(siteId);
        const numericTaskId = Number(taskId);

        if (isNaN(numericSiteId) || isNaN(numericTaskId)) {
          throw new Error("Invalid site ID or task ID");
        }

        // Use the API endpoint to get a single task
        const response = await apiClient.sites.tasksDetail2(
          numericSiteId,
          numericTaskId,
        );

        if (!response.data) {
          throw new Error("No data returned from API");
        }

        // The API returns the data in a format we don't expect, so we have to cast it
        return response.data as unknown as TaskResponse;
      } catch (error) {
        console.error("Error fetching task:", error);
        throw error;
      }
    },
    enabled: !!siteId && !!taskId,
    retry: 2, // Retry the query up to 2 times if it fails
    retryDelay: 1000, // Wait 1 second between retries
  });

  // Extract the actual task data from the response
  const task = taskResponse?.task;

  // Set the page title with the enhanced pattern
  useLinkDetailPageTitle(task?.link_domain, PAGE_TITLES.authorityLinks);

  // Initialize notes and linkUrl only on first load — never overwrite user edits on refetch
  useEffect(() => {
    if (!task) return;

    if (!notesInitialized) {
      setNotes(task.notes ?? "");
      setNotesInitialized(true);
    }

    if (task.built_link_url && !linkUrl) {
      setLinkUrl(task.built_link_url);
    }
  }, [task, notesInitialized, linkUrl]);

  // Get example links from the task
  const getExampleLinks = () => {
    if (!task?.examples) return [];

    const validExamples = task.examples.filter(
      (example) => example !== null,
    ) as string[];

    return validExamples;
  };

  // Filter example links based on search query
  const filteredExampleLinks = getExampleLinks().filter((url) =>
    url.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  // Calculate pagination
  const indexOfLastEntry = currentPage * entriesPerPage;
  const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
  const currentEntries = filteredExampleLinks.slice(
    indexOfFirstEntry,
    indexOfLastEntry,
  );
  const totalPages = Math.ceil(filteredExampleLinks.length / entriesPerPage);

  // Prepare table data
  type LinkExample = { id: string; url: string };

  const linkExamplesColumns: ColumnDef<LinkExample>[] = [
    {
      accessorKey: "url",
      header: "EXAMPLE LINKING PAGES",
      cell: (info) => {
        const value = info.getValue() as string;
        let url: URL | null = null;
        try {
          url = new URL(value);
        } catch {
          // invalid URL
        }

        return (
          <a
            target="_blank"
            href={value}
            className={styles.tableLink}
            rel="noopener noreferrer"
          >
            <Icon iconName="LinkIcon" width="1rem" height="1rem" />
            <span>{url ? shortenUrl(url.pathname) : value}</span>
          </a>
        );
      },
    },
  ];

  const linkExamplesRows: LinkExample[] = currentEntries.map((url, index) => ({
    id: String(indexOfFirstEntry + index),
    url,
  }));

  const handleFinishNicheLink = async () => {
    if (!task || isSubmitting) return;

    setIsSubmitting(true);

    try {
      const updateData: TaskAttributes = {
        status: "DONE",
      };

      // If link URL is provided, also save it
      if (linkUrl.trim()) {
        updateData.built_link_url = linkUrl.trim();
      }

      await apiClient.sites.tasksPartialUpdate(
        Number(siteId),
        Number(taskId),
        updateData,
      );

      // Update the local cache to reflect the changes without requiring a page refresh
      if (task) {
        // Create an updated task object
        const updatedTask = {
          ...task,
          status: "DONE",
          built_link_url: linkUrl.trim() || task?.built_link_url,
        };

        // Update the query cache
        queryClient.setQueryData(["nicheTask", siteId, taskId], {
          task: updatedTask,
        });
      }

      // Navigate back to the niche links list
      navigate(routes.site.links.niche({ id: siteId }));
    } catch (error) {
      console.error("Error updating niche link:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = async () => {
    if (!task || isCancelling) return;

    setIsCancelling(true);

    try {
      const updateData: TaskAttributes = {
        // @ts-expect-error TODO: not clear
        status: "CANCELLED",
      };

      await apiClient.sites.tasksPartialUpdate(
        Number(siteId),
        Number(taskId),
        updateData,
      );

      navigate(routes.site.links.niche({ id: siteId }));
    } catch (error) {
      console.error("Error cancelling niche link:", error);
    } finally {
      setIsCancelling(false);
    }
  };

  const handleSaveNotes = async () => {
    if (!task || isSavingNotes) return;

    setIsSavingNotes(true);

    try {
      const updateData: TaskAttributes = {
        notes: notes,
      };

      await apiClient.sites.tasksPartialUpdate(
        Number(siteId),
        Number(taskId),
        updateData,
      );
    } catch (error) {
      console.error("Error saving notes:", error);
    } finally {
      setIsSavingNotes(false);
    }
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  // Format probability as percentage
  const formatProbability = (value: number | null) => {
    if (value === null) return "0%";

    return `${Math.round(value)}%`;
  };

  // Count valid examples
  const examplesCount =
    task?.examples?.filter((example) => example !== null).length || 0;

  // Display loading state
  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div style={{ marginBottom: "20px" }}>
            Loading authority link details...
          </div>
        </div>
      </div>
    );
  }

  // Display error state
  if (error) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <div style={{ textAlign: "center", color: "red" }}>
          <div style={{ marginBottom: "20px", fontWeight: "bold" }}>
            Error loading authority link details
          </div>
          <div>
            {error instanceof Error ? error.message : "Unknown error occurred"}
          </div>
          <Button
            style={{ marginTop: "20px" }}
            onClick={() => window.location.reload()}
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.contentLayout}>
        <div className={styles.mainContent}>
          <Card>
            <div className={styles.linkDetailsHeader}>
              <h1 className={styles.title}>
                {task?.link_domain && (
                  <div className={styles.faviconContainer}>
                    <img
                      src={`https://www.google.com/s2/favicons?domain=${task.link_domain}&sz=32`}
                      alt=""
                      className={styles.favicon}
                      onError={(e) => {
                        // Replace with generic icon if favicon fails to load
                        (e.target as HTMLImageElement).src =
                          "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' width='32' height='32'%3E%3Cpath fill='%23ccc' d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z'/%3E%3C/svg%3E";
                      }}
                    />
                  </div>
                )}
                {task?.link_title}
              </h1>
            </div>

            <div className={styles.messageSection}>
              <p>
                Authority opportunities are relevant link placements identified
                on established websites in your market. These opportunities can
                strengthen your site's authority and topical relevance when
                acquired appropriately.
              </p>

              <div className={styles.contributorSection}>
                <div className={styles.contributorInstructionsList}>
                  <div className={styles.contributorInstructionItem}>
                    <div className={styles.stepNumber}>1</div>
                    <div className={styles.stepContent}>
                      <div className={styles.stepTitle}>
                        Review how similar links were earned
                      </div>
                      <div className={styles.stepDescription}>
                        Use the example placements to understand how comparable
                        websites secured links from this domain.
                      </div>
                    </div>
                  </div>

                  <div className={styles.contributorInstructionItem}>
                    <div className={styles.stepNumber}>2</div>
                    <div className={styles.stepContent}>
                      <div className={styles.stepTitle}>
                        Select the most appropriate approach
                      </div>
                      <div className={styles.stepDescription}>
                        Depending on the site, acquisition may involve profile
                        creation, form submission, editorial outreach, community
                        participation, or contributed content.
                      </div>
                    </div>
                  </div>

                  <div className={styles.contributorInstructionItem}>
                    <div className={styles.stepNumber}>3</div>
                    <div className={styles.stepContent}>
                      <div className={styles.stepTitle}>
                        Align the placement to your target page
                      </div>
                      <div className={styles.stepDescription}>
                        When possible, use the recommended focus keyword and
                        ranking URL so the placement supports the most relevant
                        page on your site.
                        <br />
                        Focus keyword:{" "}
                        <span className={styles.keywordHighlight}>
                          {task?.focus_keyword}
                        </span>
                        <br />
                        Ranking URL:{" "}
                        <span className={styles.urlHighlight}>
                          {task?.ranking_url}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className={styles.contributorInstructionItem}>
                    <div className={styles.stepNumber}>4</div>
                    <div className={styles.stepContent}>
                      <div className={styles.stepTitle}>
                        Save the live placement URL
                      </div>
                      <div className={styles.stepDescription}>
                        Once the link is live, paste the final URL into the form
                        so we can confirm and track the opportunity.
                      </div>
                    </div>
                  </div>
                </div>

                <div className={styles.emailTemplate}>
                  Use the example links as guidance for how this opportunity may
                  be secured, then replicate the most appropriate path for your
                  site.
                </div>
              </div>
            </div>
          </Card>

          <Card>
            <div className={styles.examplesContainer}>
              <h2>Link Examples</h2>
              <p>
                These are the pages on linking to your competitors. Study these
                example links. Try to figure out how your competitors got the
                links and then recreate that for your site.
              </p>

              {examplesCount > 0 ? (
                <>
                  <div className={styles.tableContainer}>
                    <DataTable
                      columns={linkExamplesColumns}
                      data={linkExamplesRows}
                      isLoading={false}
                      tableLayout="auto"
                    />
                  </div>

                  {/* Pagination controls */}
                  {totalPages > 1 && (
                    <div className={styles.paginationControls}>
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={styles.paginationButton}
                      >
                        Previous
                      </button>
                      <span className={styles.pageInfo}>
                        Page {currentPage} of {totalPages}
                      </span>
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={styles.paginationButton}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div
                  style={{
                    padding: "20px 0",
                    color: "#666",
                    textAlign: "center",
                  }}
                >
                  No example links available for this niche link.
                </div>
              )}
            </div>
          </Card>

          <div className={styles.notesCard}>
            <h2>Notes</h2>
            <textarea
              className={styles.notesTextarea}
              placeholder="Place your notes here for the task"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
            <Button
              className={styles.notesButton}
              onClick={handleSaveNotes}
              disabled={isSavingNotes}
            >
              {isSavingNotes ? "Saving..." : "Save Notes"}
            </Button>
          </div>
        </div>

        <div className={styles.sideContent}>
          <Card>
            <div className={styles.detailsSection}>
              <h2>Details</h2>
              <div className={styles.detailsGrid}>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>SITE:</span>
                  <span className={styles.detailValue}>{task?.link_title}</span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>LINK DOMAIN:</span>
                  <span className={styles.detailValue}>
                    {task?.link_domain ? (
                      <a
                        href={`https://${task.link_domain}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {task.link_domain}
                      </a>
                    ) : null}
                  </span>
                </div>
                {task?.link_page_url ? (
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>SIGN UP URL:</span>
                    <span className={styles.detailValue}>
                      <a
                        href={
                          task.link_page_url.startsWith("http")
                            ? task.link_page_url
                            : `https://${task.link_page_url}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {task.link_page_url}
                      </a>
                    </span>
                  </div>
                ) : null}
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>PROBABILITY:</span>
                  <span className={styles.detailValue}>
                    {task?.probability !== undefined
                      ? formatProbability(task.probability)
                      : null}
                  </span>
                </div>
                {task?.status === "DONE" && task?.built_link_url && (
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>COMPLETED LINK:</span>
                    <span className={styles.detailValue}>
                      <a
                        href={
                          task?.built_link_url.startsWith("http")
                            ? task?.built_link_url
                            : `https://${task?.built_link_url}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {task?.built_link_url}
                      </a>
                    </span>
                  </div>
                )}
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>FOCUS KEYWORD:</span>
                  <span className={styles.detailValue}>
                    {task?.focus_keyword}
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>RANKING URL:</span>
                  <span className={styles.detailValue}>
                    {task?.ranking_url ? (
                      <a
                        href={
                          task.ranking_url.startsWith("http")
                            ? task.ranking_url
                            : `https://${task.ranking_url}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {task.ranking_url}
                      </a>
                    ) : null}
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>STATUS:</span>
                  <span className={styles.detailValue}>
                    <StatusBadge status={task?.status || ""} />
                  </span>
                </div>
              </div>
            </div>

            <div style={{ marginTop: "var(--lg)", marginBottom: "var(--lg)" }}>
              <h3
                style={{
                  marginBottom: "var(--md)",
                  fontSize: "var(--text-sm)",
                  fontWeight: "400",
                }}
              >
                {(task?.status as string) === "DONE"
                  ? "UPDATE YOUR LINK"
                  : "SAVE YOUR LINK"}
              </h3>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "var(--md)",
                }}
              >
                <input
                  type="text"
                  className={styles.notesTextarea}
                  style={{
                    padding: "var(--md)",
                    minHeight: "auto",
                    border: "2px solid var(--border-color, #e0e0e0)",
                    borderRadius: "var(--radius-md, 8px)",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                    outline: "none",
                    transition: "border-color 0.2s ease",
                    backgroundColor: "var(--bg-white)",
                    color: "var(--text-black)",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor =
                      "var(--service-blue2, #90c6ff)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "var(--border-color, #e0e0e0)";
                  }}
                  placeholder="Link URL"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                />
              </div>
            </div>

            <div className={styles.actions}>
              <Button onClick={handleFinishNicheLink} disabled={isSubmitting}>
                {isSubmitting
                  ? "Saving..."
                  : (task?.status as string) === "DONE"
                    ? "Update Link"
                    : "Finish Authority Link"}
              </Button>
              {(task?.status as string) !== "DONE" && (
                <Button outlined onClick={handleCancel} disabled={isCancelling}>
                  {isCancelling ? "Cancelling..." : "Cancel"}
                </Button>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default NicheLinkDetailPage;
